import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map, filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { AppStateModel } from './shared-store/app-state.model';
import { setLoadState } from './shared-store/app-state.actions';
import * as petActions from './shared-store/pet/pet.actions';
import * as ownerActions from './shared-store/owner/owner.actions';
import * as shelterActions from './shared-store/shelter/shelter.actions';
import * as featureFlagsActions from './shared-store/feature-flags/feature-flags.actions';
import * as selectedVariablesActions from './shared-store/selected-variables/selected-variables.actions';
import { loadLatestToS } from './shared-store/selected-variables/selected-variables.actions';
import * as AuthActions from './shared-store/auth/auth.actions';
import {
  selectIsAuthenticated,
  selectToken,
} from './shared-store/auth/auth.selectors';
import { NGXLogger } from 'ngx-logger';
import { IterableAttributionService } from './service/iterable-attribution.service';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly store = inject<Store<AppStateModel>>(Store);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(NGXLogger);
  private readonly iterableAttributionService = inject(
    IterableAttributionService,
  );
  private lastToken: string | null = null;

  ngOnInit(): void {
    const url = window.location.href;
    if (!url.includes('/passwordless')) {
      this.logger.log('APP Load Auth From Storage Triggered');
      this.store.dispatch(AuthActions.loadAuthFromStorage());
    }

    combineLatest({
      isAuthed: this.store.select(selectIsAuthenticated),
      token: this.store.select(selectToken),
      state: this.store.select('loadState'),
    })
      .pipe(
        map(({ isAuthed, token, state }) => ({
          isAuthed,
          token,
          isLoaded: state.isLoaded,
        })),
        distinctUntilChanged(
          (a, b) =>
            a.isAuthed === b.isAuthed &&
            a.isLoaded === b.isLoaded &&
            a.token === b.token,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ isAuthed, token, isLoaded }) => {
        const switchedUsers =
          !!this.lastToken && !!token && token !== this.lastToken;
        this.lastToken = token ?? null;

        if (switchedUsers) {
          this.resetUserScopedState();
          this.store.dispatch(setLoadState({ isLoaded: false })); // forces boot logic below
        }

        if (isAuthed && !isLoaded) {
          this.store.dispatch(setLoadState({ isLoaded: true }));
          this.store.dispatch(petActions.loadPets());
          this.store.dispatch(ownerActions.loadOwner());
          this.store.dispatch(shelterActions.loadSheltersExtended());
          this.store.dispatch(featureFlagsActions.loadFeatureFlagTeleAdvice());
          this.store.dispatch(featureFlagsActions.loadFeatureFlagInsurance());
          this.store.dispatch(loadLatestToS());
        }

        if (!isAuthed && isLoaded) {
          this.store.dispatch(setLoadState({ isLoaded: false }));
          this.resetUserScopedState();
        }
      });

    this.resetVariablesIfAtLoginRoute();
  }

  private resetUserScopedState(): void {
    this.store.dispatch(petActions.resetPets());
    this.store.dispatch(ownerActions.resetOwner());
    this.store.dispatch(shelterActions.resetSheltersExtended());
    this.store.dispatch(featureFlagsActions.resetFeatureFlagTeleAdvice());
    this.store.dispatch(featureFlagsActions.resetFeatureFlagInsurance());
    this.store.dispatch(selectedVariablesActions.resetVariables());
  }

  private resetVariablesIfAtLoginRoute(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        filter((e) => e.urlAfterRedirects.split('?')[0] === '/'), // ✅ handles /?redirect=...
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.store.dispatch(setLoadState({ isLoaded: false }));
        this.resetUserScopedState();
      });
  }
}
